import {
  toUpper,
  values,
  find,
  prop,
  defaultTo,
  pipe,
  map,
  assoc,
  dissoc,
  path,
} from "ramda"

import * as Yup from "yup"
import { vehicleKeys, citiesData } from "@carpaddy-fixtures/index"

const CryptoJS = require("crypto-js")

//
export const ACCOUNT_DEBIT_DESC = "Account Debit"

// calculate the balances for an order
export const calculateBalances = (data: any, bal: number) => {
  let total = 0
  const balance = bal || 0

  const subtotal: any = Object.values(data).reduce((acc: any, curr: any) => {
    if (curr?.deliveryMode) {
      acc += curr.value
    }
    return acc
  }, 0)

  if (balance > subtotal) {
    total = 0
  } else if (subtotal > balance) {
    total = subtotal - balance
  }
  return { subtotal, balance, total }
}

const strictObj = {
  iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_ENCRYPT_KEY),
  mode: CryptoJS.mode.CBC,
  padding: CryptoJS.pad.Pkcs7,
}

// encrypt text
export const encrypt = (text: string | null, strict?: { strict: boolean }) =>
  text && typeof text === "string"
    ? CryptoJS.AES.encrypt(
        text,
        process.env.REACT_APP_ENCRYPT_KEY,
        strict?.strict ? strictObj : null,
      ).toString()
    : text

// decrypt text
export const decrypt = (text: string | null, strict?: { strict: boolean }) => {
  if (text && typeof text === "string") {
    try {
      const formattedText = text.replace(/ /g, "+")

      const decrypted = CryptoJS.AES.decrypt(
        formattedText,
        process.env.REACT_APP_ENCRYPT_KEY,
        strict?.strict ? strictObj : null,
      ).toString(CryptoJS.enc.Utf8)

      // If decryption results in empty string, return original text
      if (!decrypted) {
        return text
      }

      return decrypted
    } catch (error) {
      return text
    }
  } else {
    return text
  }
}
// merge two arrays together with a common uid
export const mergeArrays = (
  arrayFrom: any[],
  arrayTo: any[],
  keyToUse: string,
  key: string | undefined,
) => {
  const map = new Map()
  arrayFrom.forEach((arr: any) => map.set(arr[keyToUse], arr))
  const result = arrayTo.map((arr: any) => {
    if (key) {
      return { ...arr, [key]: map.get(arr[keyToUse]) }
    } else {
      return { ...arr, ...map.get(arr[keyToUse]) }
    }
  })
  return result
}

// used to format starting documents to be added to a new vehicle

export const startingVehicleDocuments = (
  city: keyof typeof citiesData,
  typeOfDoc: string,
  typeOfPlate: string,
) => {
  const data = citiesData[city]
    .docs(typeOfDoc, typeOfPlate)
    .reduce((_data: any, current: any) => {
      _data.push({
        key: current.key,
        expiryDate: "",
      })

      return _data
    }, [])

  return data
}

export const formatPhoneNumber = (val: any) => {
  if (!val.length) return val

  const phoneArray = val.split(" ")
  phoneArray[1] =
    phoneArray[1]?.slice(0, 1) === "0" ? phoneArray[1].slice(1) : phoneArray[1]
  const phone = phoneArray.join("")
  const formattedPhone = phone.replace("+", "")
  return formattedPhone
}

export const findCityByLG = (lg: string, citiesData: any) => {
  const formattedLG = toUpper(lg)
  const city = pipe(
    values, // @ts-ignore
    find(path(["plates", formattedLG])),
    prop("city"),
    defaultTo(null),
  )(citiesData)

  return city
}

export const findCityByPlate = (regNo: string) => {
  const newLg = regNo ? regNo.substring(0, 3) : ""
  const oldLg = regNo ? regNo.substring(5, 8) : ""

  const findbyNewLg = findCityByLG(newLg, citiesData)
  const findbyOldLg = findCityByLG(oldLg, citiesData)

  const city = findbyNewLg ? findbyNewLg : findbyOldLg ? findbyOldLg : ""

  return city
}

export const sentenceCase = (n: string) =>
  `${toUpper(n[0])}${n.slice(1, n.length)}`

export const formatVehilceDataDropDown = (vehiclesArray: any) =>
  vehiclesArray &&
  pipe(
    // prepend({ value: "", label: "" }), // @ts-ignore
    map(({ make, model, regNo, ...props }) => ({
      regNo: regNo ? regNo : "",
      label: make ? `${make} ${model} (${regNo})` : "",
      ...props,
    })),
  )(vehiclesArray)

export const formatCurrency = (total: number | any) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  }).format(total)

//used to reduce a number with 4 digits and above to decimal (works for negatives too)
export const numberShorthand = (num: number) => {
  if (num === 0 || num == null || isNaN(num) || typeof num !== "number")
    return 0

  const sign = num < 0 ? "-" : ""
  const abs = Math.abs(num)

  if (abs < 1000) {
    return num
  }
  //for thousands
  if (abs < 1000000) {
    return `${sign}${(abs / 1000).toFixed(1)}k`
  }
  //for millions
  if (abs < 1000000000) {
    return `${sign}${(abs / 1000000).toFixed(1)}m`
  }
  return `${sign}${(abs / 1000000000).toFixed(1)}b`
}

export const replaceObjectKeys = (
  obj: Record<string, any>,
  schema: Record<string, string>,
) => {
  let updatedOjb = obj
  Object.values(schema).forEach((sch: any) => {
    const [oldKey, newKey] = sch
    updatedOjb = assoc(newKey, prop(oldKey, obj), updatedOjb)
    updatedOjb = dissoc(oldKey, obj)
  })
  return updatedOjb
}

export const renderOrderNumber = (orderNumber: number) => {
  if (!orderNumber) return orderNumber
  const len = `${orderNumber}`.length + 2
  return `${orderNumber}`.padStart(len, "0").padEnd(len, "0")
}

/**
 * Returns one of:  "windows" | "macos" | "ios" | "android" | "linux" | "unknown"
 */
interface NavigatorWithUserAgentData extends Navigator {
  userAgentData?: {
    platform: string
  }
  maxTouchPoints: number
}

export const detectOS = (
  navigator: NavigatorWithUserAgentData,
): "windows" | "macos" | "ios" | "android" | "linux" | "unknown" => {
  if (navigator.userAgentData) {
    const { platform } = navigator.userAgentData
    if (/^Win/i.test(platform)) return "windows"
    if (/^Mac/i.test(platform)) return "macos"
    if (/^Linux/i.test(platform)) return "linux"
    if (/^Android/i.test(platform)) return "android"
    // iOS still reports "MacIntel" in Safari 16+, so fall through.
  }

  // fallback: userAgent
  const ua = navigator.userAgent || ""
  if (/windows phone/i.test(ua)) return "windows" // legacy
  if (/windows/i.test(ua)) return "windows"
  if (/macintosh|mac os x/i.test(ua)) {
    // Distinguish iOS Safari (iPadOS 13+ identifies as Mac)
    if (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) return "ios"
    return "macos"
  }
  if (/iphone|ipad|ipod/i.test(ua)) return "ios"
  if (/android/i.test(ua)) return "android"
  if (/linux/i.test(ua)) return "linux"

  return "unknown"
}

export const chassisValidation = Yup.string()
  .required("Enter chassis number")
  .test(
    "valid-length",
    "Chassis number must be 10, 15, 16 or 17 characters long",
    (value) => {
      if (!value) return false
      const len = value.trim().length
      return len === 10 || len === 15 || len === 16 || len === 17
    },
  )

export const getUrlRouteAndParams = (urlObj: string) => {
  if (!urlObj) return { route: "", params: {} }
  const url = JSON.parse(urlObj)
  return url
}

// Calculate the difference in days between two dates
export const getDaysDifference = (date1: Date, date2: Date): number => {
  const timeDiff = Math.abs(date2.getTime() - date1.getTime())
  return Math.ceil(timeDiff / (1000 * 3600 * 24))
}

// Extract documents with upload URLs from vehicle or license data
export const getDocumentsWithUrls = (
  data: any,
): Array<{ name: string; url: string }> => {
  const documentsWithUrls: Array<{ name: string; url: string }> = []

  // Check documents array
  if (data.documents) {
    data.documents.forEach((doc: any) => {
      const daysDifference = doc?.upload?.uploadedAt
        ? getDaysDifference(new Date(), new Date(doc?.upload?.uploadedAt))
        : 0
      if (doc?.upload?.url && daysDifference < 366) {
        const documentName = vehicleKeys()[doc.key]

        documentsWithUrls.push({
          name: documentName,
          url: doc.upload.url,
        })
      }
    })
  }

  return documentsWithUrls
}

export const channels = {
  instagram: "IG / Facebook",
  google: "Google search/ads",
  relationship: "Friends / Family",
  twitter: "X (Twitter)",
  tiktok: "Tiktok",
  linkedin: "Linkedin",
  blog: "Email / Blogs / Forums",
  flyers: "Newspaper /Flyers / Billboard / Radio",
  other: "Other",
}
